import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface SnackbarContextType {
  showSnackbar: (message: string, severity?: AlertColor) => void;
}

interface SnackbarMessage {
  key: number;
  message: string;
  severity: AlertColor;
}

const SnackbarContext = createContext<SnackbarContextType>({ showSnackbar: () => {} });

/**
 * Hook zum Anzeigen von Snackbar-Nachrichten aus jeder Komponente.
 * Verwendung: const { showSnackbar } = useSnackbar();
 */
export const useSnackbar = () => useContext(SnackbarContext);

/**
 * Globaler Snackbar-Provider für konsistentes Feedback bei Aktionen.
 * Muss in App.tsx um die gesamte App gewrapped werden.
 *
 * I18: Meldungen werden in einer FIFO-Warteschlange gehalten. Treffen zwei Meldungen
 * kurz nacheinander ein, wird die erste angezeigt und die zweite nach dem Schliessen
 * (automatisch oder per Klick) nachgereicht, statt verworfen zu werden.
 */
export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<SnackbarMessage[]>([]);
  const [open, setOpen] = useState(false);
  const keyRef = useRef(0);

  const current = queue[0];

  const showSnackbar = useCallback((msg: string, sev: AlertColor = 'success') => {
    keyRef.current += 1;
    setQueue((prev) => [...prev, { key: keyRef.current, message: msg, severity: sev }]);
    setOpen(true);
  }, []);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  // Nach der Ausblend-Animation die nächste Meldung aus der Warteschlange holen
  const handleExited = () => {
    setQueue((prev) => {
      const rest = prev.slice(1);
      if (rest.length > 0) setOpen(true);
      return rest;
    });
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        key={current?.key}
        open={open && !!current}
        autoHideDuration={5000}
        onClose={handleClose}
        slotProps={{ transition: { onExited: handleExited } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={current?.severity ?? 'success'} variant="filled" sx={{ width: '100%' }}>
          {current?.message ?? ''}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
